<template>
  <div :class="{ 'sidebar-visible': sidebarVisible }">
    <base-header class="pb-6" />
    <b-container fluid class="mt--6 max-width">
      <b-card no-body>
        <card-header title="Devices">
          <div class="d-flex justify-content-end align-items-center">
            <div class="mr-3 search-container">
              <el-input
                v-model="searchQuery"
                placeholder="Search devices..."
                prefix-icon="el-icon-search"
                clearable
                size="small"
                style="width: 250px;"
                @clear="clearSearch"
                @input="handleSearchInput"
              />
            </div>
            <b-button
              @click="toggleSidebar"
              variant="outline-secondary"
              size="sm"
              class="mr-2"
              aria-label="Toggle Filters Sidebar"
              :aria-expanded="sidebarVisible"
            >
              <i class="fas fa-filter"></i>
              Filter
              <i :class="sidebarVisible ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
                 class="transition-icon"></i>
            </b-button>
            <el-dropdown trigger="click">
              <base-button size="sm" type="info" style="font-size: 12px">
                Multi-Device Action
              </base-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item>
                  <base-button size="sm" type="info"
                               @click="MultiDeviceAction(persistentSelectedIds, 'refresh')" icon>
                    <i class="fas fa-sync-alt mr-1"></i> Refresh State ({{ persistentSelectedIds.size }})
                  </base-button>
                </el-dropdown-item>
                <el-dropdown-item>
                  <base-button size="sm" type="secondary"
                               @click="sendMultiDeviceDFU(persistentSelectedIds)" icon>
                    <i class="fas fa-cloud-download-alt mr-1"></i> Update Firmware ({{ persistentSelectedIds.size }})
                  </base-button>
                </el-dropdown-item>
                <el-dropdown-item>
                  <base-button size="sm" type="warning"
                               @click="MultiDeviceAction(persistentSelectedIds, 'vibrate')" icon>
                    <i class="fas fa-bell mr-1"></i> Vibrate ({{ persistentSelectedIds.size }})
                  </base-button>
                </el-dropdown-item>
                <el-dropdown-item>
                  <base-button size="sm" type="danger"
                               @click="MultiDeviceAction(persistentSelectedIds, 'reboot')" icon>
                    <i class="fas fa-bell mr-1"></i> Reboot ({{ persistentSelectedIds.size }})
                  </base-button>
                </el-dropdown-item>
                <el-dropdown-item divided>
                  <base-button size="sm" type="info"
                               @click="AllDeviceAction(activeEntity?.id, 'refresh')" icon>
                    <i class="fas fa-sync-alt mr-1"></i> Refresh State (All)
                  </base-button>
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </card-header>

        <div class="px-3 py-2 border-bottom d-flex flex-wrap align-items-center" style="gap:12px;">
          <div>
            <label class="small font-weight-bold text-muted mb-0">Entity</label>
            <b-form-select v-model="selectedEntity" size="sm" :options="entitySelectOptions"
                           class="mr-2" style="width:150px;">
              <template #first>
                <b-form-select-option :value="null">All Entities</b-form-select-option>
              </template>
            </b-form-select>
          </div>
          <div>
            <label class="small font-weight-bold text-muted mb-0">Site</label>
            <b-form-select v-model="selectedSite" size="sm" :options="siteSelectOptions"
                           class="mr-2" style="width:150px;" :disabled="!selectedEntity">
              <template #first>
                <b-form-select-option :value="null">All Sites</b-form-select-option>
              </template>
            </b-form-select>
          </div>
          <div>
            <label class="small font-weight-bold text-muted mb-0">Group</label>
            <b-form-select v-model="selectedGroup" size="sm" :options="groupSelectOptions"
                           class="mr-2" style="width:150px;" :disabled="!selectedSite">
              <template #first>
                <b-form-select-option :value="null">All Groups</b-form-select-option>
              </template>
            </b-form-select>
          </div>
          <div class="ml-auto text-muted small">{{ filteredDevices.length }} device(s)</div>
        </div>

        <transition name="slide-fade">
          <filter-sidebar v-if="sidebarVisible" ref="filterSidebar"
                          :title="'Device Filters'" :filters-config="deviceFiltersConfig"
                          :visible="sidebarVisible" v-model="activeFilters"
                          @filter-change="handleFilterChange" @filter-reset="handleFilterReset" />
        </transition>

        <el-table header-row-class-name="thead-light"
                  :data="paginatedDevices"
                  @selection-change="handleSelectionChange"
                  ref="devicesTable" :row-key="'name'"
                  :row-class-name="getRowClassName" class="table-with-transition">
          <template #empty>
            <span v-if="loading">Loading Devices <i class="fas fa-sync-alt fa-spin mr-1"></i></span>
            <span v-else>No Devices Found</span>
          </template>
          <el-table-column type="selection" width="55" :reserve-selection="true" />
          <el-table-column header-class-name="fixed-header" class-name="font-weight-bold" label="Device ID" sortable prop="name" />
          <el-table-column label="Entity" sortable prop="entity" />
          <el-table-column label="Site" sortable prop="site" />
          <el-table-column label="Group" sortable prop="group" />
          <el-table-column label="Version" prop="fw_version">
            <template v-slot="{ row }">{{ row.fw_version }}</template>
          </el-table-column>
          <el-table-column label="Status">
            <template v-slot="{ row }">
              <span :class="getDeviceStatusClass(row.state.current)" class="font-weight-bold">{{ row.state.current }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Created Date">
            <template v-slot="{ row }">{{ formatDate(row.created_at) }}</template>
          </el-table-column>
          <el-table-column label="Last Updated">
            <template v-slot="{ row }">
              <span :class="DeviceUnresponsive(row.updated_at) ? 'text-danger' : ''">{{ formatTimeAgo(row.updated_at) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Battery">
            <template slot-scope="{ row }">
              <div class="battery-cell">
                <span v-html="batteryFormatter(row.battery)"></span>
                <i v-if="row.is_charging" class="ml-1 fas fa-bolt charging-icon"></i>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="Actions" width="320px">
            <template #default="{ row }">
              <base-button :type="(row.current_provision && !isProvisionStale(row)) ? 'info' : 'primary'" size="sm"
                           @click="handleProvisionClick(row)">
                <span v-if="row.current_provision && !isProvisionStale(row)"><i class="fas fa-sync-alt fa-spin mr-1"></i>Provisioning...</span>
                <span v-else><i class="fas fa-cogs mr-1"></i>Provision</span>
              </base-button>
              <base-button :disabled="DeviceUnresponsive(row.updated_at)" size="sm"
                           type="outline-warning" @click="cancelProvisioning(row.name)" icon>
                <i class="fas fa-ban mr-1"></i>
              </base-button>
              <base-button size="sm" type="danger" @click="onDelete(row)" icon>
                <i class="fas fa-trash-alt mr-1"></i>
              </base-button>
              <el-dropdown trigger="click">
                <base-button size="sm" type="info" icon><i class="fas fa-ellipsis-v"></i></base-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item>
                    <base-button size="sm" type="secondary" @click="onEdit(row)" icon>
                      <i class="fas fa-edit mr-1"></i> Edit
                    </base-button>
                  </el-dropdown-item>
                  <el-dropdown-item divided>
                    <base-button :disabled="DeviceUnresponsive(row.updated_at)" size="sm" type="info"
                                 @click="sendMqttGeneralCommand(row, 'refresh')">
                      <i class="fas fa-sync-alt mr-1"></i> Refresh
                    </base-button>
                  </el-dropdown-item>
                  <el-dropdown-item>
                    <base-button :disabled="DeviceUnresponsive(row.updated_at)" size="sm" type="warning"
                                 @click="sendMqttGeneralCommand(row, 'vibrate')">
                      <i class="fas fa-bell mr-1"></i> Vibrate
                    </base-button>
                  </el-dropdown-item>
                  <el-dropdown-item>
                    <base-button :disabled="DeviceUnresponsive(row.updated_at)" size="sm" type="primary"
                                 @click="sendMqttDFU(row)">
                      <i class="fas fa-cloud-download-alt mr-1"></i> Update Firmware
                    </base-button>
                  </el-dropdown-item>
                  <el-dropdown-item>
                    <base-button :disabled="DeviceUnresponsive(row.updated_at)" size="sm" type="danger"
                                 @click="sendMqttGeneralCommand(row, 'reboot')">
                      <i class="fas fa-power-off mr-1"></i> Reboot
                    </base-button>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>

        <div style="align-self: center;">
          <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange"
                         :current-page="currentPage" :page-sizes="[10, 20, 50]"
                         :page-size="pageSize" layout="prev, pager, next" :total="filteredDevices.length" />
        </div>
      </b-card>
    </b-container>
  
    <ModalProvisionFileSelect
      v-if="isFileSelectVisible"
      :device-id="currentProvisioningDevice?.name"
      :entity="currentProvisioningDevice?.entity"
      @start-provision="onFilesSelected"
      @close-modal="closeFileSelectModal"
    />
    <ModalProvision
      ref="provisioningModal" 
      v-if="isProvModalVisible"
      :device-id="currentProvisioningDevice?.name"
      :entity="currentProvisioningDevice?.entity"
      @close-modal="closeProvisioningModal"
    />
  </div>
</template>

<script>
import { Routes } from "@/router";
import { Action } from "@/store/actions";
import FilterSidebar from "../../views/components/FilterSidebar.vue";
import { mapState, mapGetters } from "vuex";
import {
  Table, TableColumn, Dropdown, DropdownMenu, DropdownItem, Pagination, Input
} from "element-ui";
import Swal from "sweetalert2";
import { confirmDelete } from "@/helper";
import ModalProvision from "../../views/components/ModalProvision.vue";
import ModalProvisionFileSelect from "../../views/components/ModalProvisionFileSelect.vue";

export default {
  name: "DeviceList",
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Pagination.name]: Pagination,
    [Input.name]: Input,
    ModalProvision,
    FilterSidebar,
    ModalProvisionFileSelect,
  },
  data() {
    return {
      searchQuery: '',
      refreshInterval: null,
      sidebarVisible: false,
      activeFilters: {
        status: [], battery: [], firmwareVersion: [], dateRange: null, activeStatus: []
      },
      selectedEntity: null,
      selectedSite: null,
      selectedGroup: null,
      selectedDevices: [],
      persistentSelectedIds: new Set(),
      filterActive: false,
      currentProvisioningDevice: null,
      isProvModalVisible: false,
      isFileSelectVisible: false,
      _pendingProvision: null,
      loading: false,
      pageSize: 20,
      currentPage: 1,
      deviceFiltersConfig: [
        { id: "status", label: "Device Status", type: "checkbox", options: [] },
        { id: "battery", label: "Battery", type: "checkbox", options: [
            { value: "High", text: "High" }, { value: "Medium", text: "Medium" },
            { value: "Low", text: "Low" }, { value: "Charging", text: "Charging" }
        ]},
        { id: "firmwareVersion", label: "Firmware Version", type: "checkbox", options: [] },
        { id: "dateRange", label: "Date Range", type: "dropdown", options: [
            { value: "24_hours", text: "Last 24 Hours" },
            { value: "3_days", text: "Last 3 Days" },
            { value: "week", text: "Last Week" },
            { value: "month", text: "Last Month" },
            { value: "3_months", text: "Last 3 Months" },
            { value: "6_months", text: "Last 6 Months" },
            { value: "year", text: "Last Year" }
        ]},
        { id: "activeStatus", label: "Active Status", type: "checkbox", options: [
            { value: "active", text: "Active" },
            { value: "inactive", text: "Inactive" }
        ]},
      ],
    };
  },
  computed: {
    ...mapState({
      devices: s => s.device.devices,
    }),
    ...mapGetters(["activeEntity"]),

    entitySelectOptions() {
      const entities = [...new Set(this.devices.map(d => d.entity))].filter(Boolean);
      return entities.map(e => ({ value: e, text: e }));
    },
    siteSelectOptions() {
      if (!this.selectedEntity) return [];
      const filtered = this.devices.filter(d => d.entity === this.selectedEntity);
      const sites = [...new Set(filtered.map(d => d.site).filter(Boolean))];
      return sites.map(s => ({ value: s, text: s }));
    },
    groupSelectOptions() {
      if (!this.selectedEntity || !this.selectedSite) return [];
      const groups = [...new Set(
        this.devices
          .filter(d => d.entity === this.selectedEntity && d.site === this.selectedSite)
          .map(d => d.group).filter(Boolean)
      )];
      return groups.map(g => ({ value: g, text: g }));
    },
    filteredDevices() {
      let filtered = this.devices;
      if (this.selectedEntity) filtered = filtered.filter(d => d.entity === this.selectedEntity);
      if (this.selectedSite) filtered = filtered.filter(d => d.site === this.selectedSite);
      if (this.selectedGroup) filtered = filtered.filter(d => d.group === this.selectedGroup);

      if (this.activeFilters.status?.length) filtered = filtered.filter(d => this.activeFilters.status.includes(d.state?.current));
      if (this.activeFilters.firmwareVersion?.length) filtered = filtered.filter(d => this.activeFilters.firmwareVersion.includes(d.fw_version));
      if (this.activeFilters.battery?.length) {
        filtered = filtered.filter(device => {
          if (this.activeFilters.battery.includes("High") && device.battery > 75) return true;
          if (this.activeFilters.battery.includes("Medium") && device.battery > 50 && device.battery <= 75) return true;
          if (this.activeFilters.battery.includes("Low") && device.battery <= 50) return true;
          if (this.activeFilters.battery.includes("Charging") && device.is_charging) return true;
          return false;
        });
      }
      if (this.activeFilters.dateRange) {
        const now = new Date();
        let startDate;
        switch (this.activeFilters.dateRange) {
          case "24_hours": startDate = new Date(now - 24*60*60*1000); break;
          case "3_days": startDate = new Date(now - 3*24*60*60*1000); break;
          case "week": startDate = new Date(now - 7*24*60*60*1000); break;
          case "month": startDate = new Date(now - 30*24*60*60*1000); break;
          case "3_months": startDate = new Date(now - 90*24*60*60*1000); break;
          case "6_months": startDate = new Date(now - 180*24*60*60*1000); break;
          case "year": startDate = new Date(now - 365*24*60*60*1000); break;
        }
        if (startDate) filtered = filtered.filter(d => new Date(d.created_at) >= startDate && new Date(d.created_at) <= now);
      }
      if (this.activeFilters.activeStatus?.length) {
        filtered = filtered.filter(device => {
          const isActive = !this.DeviceUnresponsive(device.updated_at);
          const selected = this.activeFilters.activeStatus;
          if (selected.includes('active') && selected.includes('inactive')) return true;
          if (selected.includes('active')) return isActive;
          if (selected.includes('inactive')) return !isActive;
          return true;
        });
      }
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        filtered = filtered.filter(d => 
          (d.name && d.name.toLowerCase().includes(q)) ||
          (d.entity && d.entity.toLowerCase().includes(q)) ||
          (d.site && d.site.toLowerCase().includes(q)) ||
          (d.group && d.group.toLowerCase().includes(q)) ||
          (d.fw_version && d.fw_version.toLowerCase().includes(q)) ||
          (d.state && d.state.current && d.state.current.toLowerCase().includes(q))
        );
      }
      return filtered;
    },
    paginatedDevices() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredDevices.slice(start, start + this.pageSize);
    },
    dynamicStatusOptions() {
      const statuses = new Set(this.devices.map(d => d.state?.current));
      return Array.from(statuses).map(s => ({ value: s, text: s }));
    },
    firmwareVersionOptions() {
      const versions = new Set(this.devices.map(d => d.fw_version));
      return Array.from(versions);
    },
  },
  watch: {
    dynamicStatusOptions: {
      immediate: true,
      handler(newVal) {
        const idx = this.deviceFiltersConfig.findIndex(f => f.id === "status");
        if (idx !== -1) this.deviceFiltersConfig[idx].options = newVal;
      },
    },
    firmwareVersionOptions: {
      immediate: true,
      handler(newVal) {
        const idx = this.deviceFiltersConfig.findIndex(f => f.id === "firmwareVersion");
        if (idx !== -1) this.deviceFiltersConfig[idx].options = newVal;
      },
    },
    selectedEntity(val) {
      this.currentPage = 1;
      this.selectedSite = null;
      this.selectedGroup = null;
    },
    selectedSite(val) {
      this.currentPage = 1;
      this.selectedGroup = null;
    },
    selectedGroup() {
      this.currentPage = 1;
    },
  },
  methods: {
    isProvisionStale(row) {
      if (!row.current_provision) return false;
      const startedAt = row.current_provision.started_at;
      if (!startedAt) return false;
      const minutesAgo = (Date.now() - new Date(startedAt)) / 1000 / 60;
      return minutesAgo > 15;
    },
    getDeviceStatusClass(status) {
      switch (status) {
        case "ALLOCATED": return "text-success";
        case "PROVISIONING": case "DFU": return "text-primary";
        case "ERROR": case "DEFECT": case "UNAVAILABLE": return "text-danger";
        case "BOOTING": case "WAITING": return "text-warning";
        default: return "text-neutral";
      }
    },
    getRowClassName({ row }) {
      return this.DeviceUnresponsive(row.updated_at) ? "warning-row" : "";
    },
    DeviceUnresponsive(datestring) {
      if (!datestring) return true;
      return new Date(datestring) < new Date(Date.now() - 20 * 60 * 1000);
    },
    formatTimeAgo(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      const seconds = Math.floor((Date.now() - date) / 1000);
      if (seconds < 5) return "just now";
      if (seconds < 60) return `${seconds} seconds ago`;
      if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
      if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
      if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
      if (seconds < 2592000) return `${Math.floor(seconds / 604800)} weeks ago`;
      if (seconds < 31536000) return `${Math.floor(seconds / 2592000)} months ago`;
      return `${Math.floor(seconds / 31536000)} years ago`;
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
    },
    batteryFormatter(battery) {
      if (battery > 75) return `<span style="color:green">${battery}%</span>`;
      if (battery > 50) return `<span style="color:orange">${battery}%</span>`;
      return `<span style="color:red">${battery}%</span>`;
    },
    handleSearchInput() {
      this.currentPage = 1;
    },
    clearSearch() {
      this.searchQuery = '';
      this.currentPage = 1;
    },
    toggleSidebar() { this.sidebarVisible = !this.sidebarVisible; },
    handleFilterChange({ filterId, value }) {
      this.activeFilters[filterId] = value;
      this.currentPage = 1;
    },
    handleFilterReset() {
      this.activeFilters = { status: [], battery: [], firmwareVersion: [], dateRange: null, activeStatus: [] };
      this.currentPage = 1;
    },
    handleFilterRemove(filterId, filterValue) {
      if (Array.isArray(this.activeFilters[filterId])) {
        this.activeFilters[filterId] = this.activeFilters[filterId].filter(v => v !== filterValue);
      } else {
        this.activeFilters[filterId] = null;
      }
      this.currentPage = 1;
    },
    handleSizeChange(newSize) {
      this.pageSize = newSize;
      this.currentPage = 1;
    },
    handleCurrentChange(newPage) {
      this.currentPage = newPage;
    },
    async loadDevice() {
      this.loading = true;
      try {
        await this.$store.dispatch(Action.device.load_all, { limit: 0, offset: 0 });
        this.loading = false;
        this.$nextTick(() => this.restoreSelection());
      } catch (e) {
        console.error(e);
        this.loading = false;
      }
    },
    updatePersistentSelection() {
      this.persistentSelectedIds = new Set(this.selectedDevices.map(d => d.name));
    },
    restoreSelection() {
      if (!this.persistentSelectedIds.size) return;
      this.$refs.devicesTable?.clearSelection();
      const toSelect = this.filteredDevices.filter(d => this.persistentSelectedIds.has(d.name));
      toSelect.forEach(d => this.$refs.devicesTable?.toggleRowSelection(d, true));
    },
    handleSelectionChange(selectedRows) {
      this.selectedDevices = selectedRows;
      this.updatePersistentSelection();
    },
    clearPersistentSelection() {
      this.persistentSelectedIds.clear();
      this.selectedDevices = [];
      this.$refs.devicesTable?.clearSelection();
    },
    handleProvisionClick(row) {
      if (row.current_provision) {
        this.openProvisioningModal(row);
      } else {
        this.currentProvisioningDevice = { ...row };
        this.isFileSelectVisible = true;
      }
    },
    openProvisioningModal(row) {
      this.currentProvisioningDevice = { ...row };
      this.isProvModalVisible = true;
    },
    closeFileSelectModal() {
      this.isFileSelectVisible = false;
      if (this._pendingProvision) {
        const { device, commands } = this._pendingProvision;
        this._pendingProvision = null;
        this._startProvisionPost(device, commands);
      }
    },
    onFilesSelected(selectedCommands) {
      const device = { ...this.currentProvisioningDevice };
      this._pendingProvision = { device, commands: selectedCommands };
    },
    async _startProvisionPost(device, commands) {
      try {
        await this.$http.post(`/tenant/${device.entity}/provision/${device.name}`, { commands });
        this.currentProvisioningDevice = device;
        this.isProvModalVisible = true;
      } catch (error) {
        console.error('Failed to start provisioning:', error);
        this.$notify({ title: 'Error', message: `Provisioning failed: ${error.response?.data?.error || error.message}`, type: 'error' });
      }
    },
    closeProvisioningModal() {
      this.currentProvisioningDevice = null;
      this.isProvModalVisible = false;
      this.loadDevice();
    },
    async cancelProvisioning(deviceName) {
      try {
        await this.$store.dispatch(Action.device.cancelProvisionProcess, deviceName);
        if (this.currentProvisioningDevice?.name === deviceName) this.closeProvisioningModal();
        this.$notify({ title: "Provisioning Cancelled", message: `Provisioning for ${deviceName} cancelled.`, type: "info" });
      } catch (error) {
        console.error("Cancel failed:", error);
        this.$notify({ title: "Error", message: error.message || error, type: "error" });
      }
    },
    async AllDeviceAction(entity, type) { /* keep same as before */ },
    async sendMqttGeneralCommand(row, type) { /* keep same as before */ },
    async sendMqttDFU(row) { /* keep same as before */ },
    async MultiDeviceAction(device_ids, type) { /* keep same as before */ },
    async sendMultiDeviceDFU(device_ids) { /* keep same as before */ },
    onNew(type) { /* keep same as before */ },
    onEdit(row) { this.$router.push({ name: Routes.sDevicesEdit, params: { name: row.name } }); },
    onDelete(row) { confirmDelete().then(result => { if (result.value) this.$store.dispatch(Action.device.delete, row); }); },
    deleteSelected() { confirmDelete().then(result => { if (result.value) { this.selectedDevices.forEach(d => this.$store.dispatch(Action.device.delete, d)); this.selectedDevices = []; } }); },
  },
  mounted() {
    this.loadDevice();
    this.refreshInterval = setInterval(() => this.loadDevice(), 30000);
  },
  beforeDestroy() {
    clearInterval(this.refreshInterval);
  },
};
</script>

<style>
/* Keep existing styles and add sticky header CSS if not already */
.search-container {
  margin-right: 1rem;
}
.el-table {
  overflow: visible !important;
}
.el-table__header-wrapper {
  position: sticky;
  top: 70px;
  z-index: 50;
  background-color: #f5f7fa;
}
.el-table__header-wrapper th.el-table__cell {
  background-color: #f5f7fa;
}
</style>
